<template>
  <div class="email-form-input-wrap" :class="{ disabled: disabled }">
    <div class="input-container communication-control" @click="onInputContainerClick">
      <div
        class="list-element"
        v-for="(company, index) in value"
        :key="index"
        :class="{ 'is-selected': selectedItem && selectedItem.id === company.id }"
      >
        <div class="list-element-contentwrap">
          <span class="list-element--companyname">{{ company.name }}</span>
          <span v-if="company.email" class="list-element--email">{{ company.email }}</span>
          <span v-else-if="company.created_by && company.created_by.email" class="list-element--email">{{ company.created_by.email }}</span>
          <span v-else class="list-element--email">{{ $gettext('No email') }}</span>
        </div>
        <button class="remove-element-button" type="button" @click="removeCompany(company)">
          <font-awesome-icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <input
        id="forminput-box"
        ref="forminput-box"
        @input="handleInput"
        class="input ghost-input"
        type="text"
        v-model="query"
        :placeholder="value.length === 0 ? placeholder : ''"
        :required="value.length === 0"
        autocomplete="off"
        @keydown.down.prevent="onArrowPress()"
        @keydown.up.prevent="onArrowPress()"
        @keydown.backspace="onBackspace"
        :disabled="disabled"
      />
    </div>

    <div class="relative-container">
      <DropdownContainer
        :showContainer="companies.length > 0 || query.length > 2"
        @closeDropdown="() => (companies = [])"
      >
        <CustomerSelector
          v-if="query"
          v-model="query"
          :exclude-ids="value.map((item) => item.id)"
          :style-options="{ size: 'md', height: '300px', width: '300px' }"
          focusable
          show-logo
          show-email
          ref="customer-selector"
          @customer-selected="
            (company) => {
              addCompany(company.customer)
            }
          "
        />
      </DropdownContainer>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import DropdownContainer from '@/components/ui/DropdownContainer.vue'
import CustomerSelector from '@/components/accountants/CustomerSelector'

export default {
  name: 'CompanySearchInput',
  components: { DropdownContainer, CustomerSelector },
  props: {
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      query: '',
      companies: [],
      selectedItem: null
    }
  },
  watch: {
    disabled (newValue, oldValue) {
      this.query = ''
    }
  },
  methods: {
    ...mapActions({
      getAccountantConnections: 'accountants/getAccountantConnections',
    }),
    handleInput() {
      // Typing clears any selection
      if (this.selectedItem) this.selectedItem = null

      this.getAccountantConnections({ search: this.query, status: 'connected' }).then((response) => {
        this.companies = response.data.results.map((connection) => connection.customer)
      })
    },
    onInputContainerClick (e) {
      const inputEl = this.$refs['forminput-box']

      // If the click happened inside a .list-element, ignore it entirely
      if (e.target.closest('.list-element')) {
        return
      }

      // Check current focus
      const active = document.activeElement
      const isInputFocused = inputEl && (active === inputEl || inputEl.contains(active))

      if (!isInputFocused && !this.disabled) {
        // 👉 Do your "something" here
        inputEl.focus()
        // or whatever behavior you want
      }
    },
    addCompany(company) {
      this.$emit('input', [...this.value, company])
      this.query = ''
      this.companies = []
      this.selectedItem = null // ensure no selection lingers
      // focus back to the main input
      if (this.$refs['forminput-box']) {
        this.$refs['forminput-box'].focus()
      }
    },
    removeCompany(company) {
      // Clear selection if we're removing the selected one
      if (this.selectedItem && this.selectedItem.id === company.id) {
        this.selectedItem = null
      }
      this.$emit(
        'input',
        this.value.filter((item) => item.id !== company.id)
      )
    },
    onArrowPress() {
      if (this.$refs['customer-selector']) {
        this.$refs['customer-selector'].focusOnList(0)
      }
    },
    onBackspace(e) {
      if (this.query.length > 0) return
      if (this.value.length === 0) return

      const last = this.value[this.value.length - 1]

      if (!this.selectedItem) {
        // First backspace: select the last chip
        this.selectedItem = last
        return
      }

      // Second backspace (while selected): remove it
      // (Also handles if selection somehow isn't the last: we remove the actual last)
      if (this.selectedItem.id === last.id) {
        this.$emit('input', this.value.slice(0, -1))
        this.selectedItem = null
      } else {
        // Re-sync selection to last if out of sync
        this.selectedItem = last
      }
    },
  },
}
</script>

<style lang="scss">
.email-form-input-wrap {
  width: 100%;
}
</style>
<style lang="scss" scoped>
.email-form-input-wrap {
  &.disabled {
    cursor: not-allowed;
    input { pointer-events: none; }
    .input-container {
      #forminput-box {
        &::placeholder {
          color: $colorWarning;
        }
      }
    }
  }
  .input-container {
    display: flex;
    flex-direction: row;
    position: relative;
    overflow: auto;
    max-height: 13rem;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
    border-radius: 6px;
    padding: 0.375rem 0.75rem;
    gap: 0.5rem;
    #forminput-box {
      font-size: 1rem;
      &::placeholder {
        color: $inputPlaceholderColor;
        font-size: 1rem;
      }
    }
  }

  @media (max-width: 840px) {
    .input-container {
      max-height: 6rem;
    }
  }

  input {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 150px;
    border: none;

    &.ghost-input {
      background: transparent;

      &:focus {
        outline: none;
      }
    }
  }

  .list-element {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    background: white;
    border-radius: $radiusXl;
    font-size: 14px;
    box-shadow: $emailInputItemShadow;
    padding: 0;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    &.is-selected {
      background: #c2e7ff;
      box-shadow: $emailInputItemSelectedShadow;
    }
    .list-element-contentwrap {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
    &--companyname,&--email {
      line-height: 1;
    }
    &--companyname {

    }
    &--email {
      font-size: 12px;
      opacity: 0.7;
    }
    .remove-element-button {
      display: flex;
      border: none;
      background: none;
      font-size: 15px;
      opacity: 0.6;
      padding: 0;
    }
  }

  .relative-container {
    position: relative;
  }

  .danger-box {
    background-color: $colorDangerBg;
  }

  .customer-list {
    max-height: 300px;
    overflow: visible !important;
  }

  .customer-container {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
    cursor: pointer;

    &:hover {
      background-color: #f1f1f1;
    }

    .avatar-container {
      .b-avatar {
        width: 2rem;
        height: 2rem;
      }
    }

    .customer {
      &--name {
        font-size: 1rem;
      }

      &--email {
        font-size: 14px;
        opacity: 0.6;
      }
    }
  }
}
</style>
